/**
 * The location page's Tests & results section.
 *
 * Lifted verbatim out of `sites/trade-panel.tsx` (#1011) as part of the
 * pure-move commit: no behaviour changed here, so the diff is a rename
 * and the only question a reviewer has to answer is "did anything
 * change?". The IA flip that consumes it is the next commit.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { dateSortValue } from "@/lib/table-sort";
import { openExternalLink } from "@/lib/standalone";
import { AttachmentPreviewDialog } from "@/components/attachment-preview-dialog";
import { Badge } from "@/components/ui/badge";
import { DataTable, useSortedRows, type Column } from "@/components/ui/data-table";
import { cn, formatDate } from "@/lib/utils";
import { ClipboardCheck, ExternalLink } from "lucide-react";
import { CollapsibleSection, ShowMoreFooter } from "./section-chrome";
import { DEFAULT_VISIBLE_ROWS, type SiteContext, type TestRow, type TradeSummary } from "./types";
import { testStateLabel } from "@/lib/test-state";

/**
 * ── The conditional-hooks fix ────────────────────────────────────────────
 *
 * This component used to open with
 *
 *     if (tradeCode !== "backflow" && tradeCode !== "fire_ext") return null;
 *     const [expanded, setExpanded] = useState(false);   // AFTER the return
 *     const q = useQuery({ ... });                       // AFTER the return
 *
 * which is a conditional-hooks violation that was harmless only because
 * `tradeCode` was fixed for the life of each mounted instance: one trade,
 * one tab, one component. Now that trade is a FILTER, a single instance
 * sees backflow -> plumbing -> backflow, the hook count changes between
 * renders, and React throws "rendered fewer hooks than expected".
 *
 * So the gate left the component and became data (`tabs.ts`
 * TESTABLE_TRADES / `testsUnavailableReason`). The section renders
 * unconditionally; the CALLER decides what to say. Hooks run every time.
 */
export function TestsSection({
    site,
    trade,
    summary,
    siteOverdueCount = 0,
}: {
    site: SiteContext;
    trade: string | null;
    summary: TradeSummary | null;
    /**
     * Site-wide overdue, used when no trade filter is active. Without it the
     * tab TRIGGER would read "3 overdue" while the section header under it
     * read nothing — two numbers about the same devices, one of them blank.
     */
    siteOverdueCount?: number;
}) {
    const [expanded, setExpanded] = useState(false);
    const [previewAttachment, setPreviewAttachment] = useState<{ id: string; fileName: string } | null>(null);
    const q = useQuery({
        queryKey: ["site-trade-tests", site.siteId, trade],
        queryFn: () => {
            // No trade = both families, which the route already supports
            // (an empty `trade` means wantBackflow AND wantFire). Sending
            // `trade=all` would match neither.
            const qs = trade ? `trade=${encodeURIComponent(trade)}&limit=200` : "limit=200";
            return api.get<{ tests: TestRow[]; total: number }>(`/api/sites/${site.siteId}/tests?${qs}`);
        },
        enabled: !!site.siteId,
    });

    const overdueCount = summary ? summary.overdue_count : siteOverdueCount;
    const rows = q.data?.tests ?? [];
    const total = q.data?.total ?? 0;

    const columns: Column<TestRow>[] = [
        {
            key: "performed_at",
            header: "Date",
            sortable: true,
            firstSortDir: "desc",
            sortAccessor: (r) => dateSortValue(r.performed_at),
            render: (r) => (
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="tabular-nums">{formatDate(r.performed_at)}</span>
                    {r.source === "st_backfill" && (
                        <Badge
                            variant="outline"
                            className="text-[9px] uppercase border-amber-500/40 text-amber-700 bg-amber-500/10"
                            title="Reconstructed from an imported internal test form. Refer to the original report for compliance."
                        >
                            ST
                        </Badge>
                    )}
                    {r.source === "vepo" && (
                        <Badge
                            variant="outline"
                            className="text-[9px] uppercase border-sky-500/40 text-sky-700 bg-sky-500/10"
                            title="Imported from a VEPO city-portal record."
                        >
                            VEPO
                        </Badge>
                    )}
                </div>
            ),
        },
        {
            key: "asset_name",
            header: "Device",
            sortable: true,
            render: (r) =>
                r.asset_id ? (
                    <Link to={`/assets/${r.asset_id}`} className="hover:underline">
                        <div className="font-medium">{r.asset_name ?? "—"}</div>
                        {r.serial_number && (
                            <div className="text-xs text-foreground/80 font-mono">SN {r.serial_number}</div>
                        )}
                    </Link>
                ) : (
                    <span>{r.asset_name ?? "—"}</span>
                ),
        },
        {
            key: "result",
            header: "Result",
            sortable: true,
            sortAccessor: (r) => (r.overall_result ?? "").toLowerCase() || null,
            render: (r) => {
                const v = (r.overall_result ?? "").toLowerCase();
                return v ? (
                    <Badge
                        variant="outline"
                        className={cn(
                            "uppercase text-[10px]",
                            v === "pass" && "border-emerald-500/40 text-emerald-600 bg-emerald-500/10",
                            v === "fail" && "border-destructive/40 text-destructive bg-destructive/10",
                        )}
                    >
                        {testStateLabel(v)}
                    </Badge>
                ) : (
                    <span className="text-foreground/80">—</span>
                );
            },
        },
        {
            key: "job",
            header: "Job",
            sortable: true,
            sortAccessor: (r) => (r.job_id ? (r.job_number ?? r.job_id.slice(0, 8)) : null),
            render: (r) =>
                r.job_id ? (
                    <Link to={`/jobs/${r.job_id}`} className="text-primary font-mono text-xs hover:underline">
                        #{r.job_number ?? r.job_id.slice(0, 8)}
                    </Link>
                ) : (
                    <span className="text-foreground/80">—</span>
                ),
        },
        {
            key: "pdf",
            header: "PDF",
            render: (r) => {
                if (r.drive_file_id) {
                    return (
                        <button
                            type="button"
                            onClick={() => openExternalLink(`https://drive.google.com/file/d/${r.drive_file_id}/view`)}
                            className="text-xs inline-flex items-center gap-1 text-primary hover:underline"
                        >
                            <ExternalLink className="h-3 w-3" /> Drive
                        </button>
                    );
                }
                if (r.pdf_attachment_id) {
                    return (
                        <button
                            type="button"
                            onClick={() =>
                                setPreviewAttachment({
                                    id: r.pdf_attachment_id!,
                                    fileName: "Test report",
                                })
                            }
                            className="text-xs inline-flex items-center gap-1 text-primary hover:underline"
                        >
                            <ExternalLink className="h-3 w-3" /> PDF
                        </button>
                    );
                }
                return <span className="text-foreground/80 text-xs">—</span>;
            },
        },
    ];

    // Sort every row, THEN cut to the preview, so a sort reaches past the
    // first few rows. Remembered per user.
    const { sorted, controlledSort } = useSortedRows(rows, columns, { sortId: "site.tests" });
    const visibleRows = expanded ? sorted : sorted.slice(0, DEFAULT_VISIBLE_ROWS);

    return (
        <CollapsibleSection
            title={trade === "fire_ext" ? "Inspections" : "Tests"}
            icon={<ClipboardCheck className="h-4 w-4 text-foreground/70" />}
            count={total}
            // `defaultOpen={overdue_count > 0}` was right when this was one
            // of four sections stacked inside a trade tab: the page opened
            // short, and a compliance gap was the one thing that forced
            // itself open. It is the WHOLE TAB BODY now, so collapsed-by-
            // default would mean clicking "Tests & results" and landing on a
            // closed accordion. The auto-open rule didn't disappear — it was
            // promoted to the default ACTIVE TAB (see location/tabs.ts).
            defaultOpen
            badge={
                overdueCount > 0 ? (
                    <Badge
                        variant="outline"
                        className="border-destructive/40 bg-destructive/10 text-[10px] text-destructive"
                    >
                        {overdueCount} overdue
                    </Badge>
                ) : undefined
            }
        >
            <DataTable
                columns={columns}
                data={visibleRows}
                keyField="id"
                controlledSort={controlledSort}
                loading={q.isLoading}
                emptyMessage={
                    trade === "fire_ext"
                        ? "No inspections on file yet."
                        : "No tests on file yet. Tests appear here automatically once a test job is completed."
                }
            />
            <ShowMoreFooter total={total} expanded={expanded} onToggle={() => setExpanded((v) => !v)} />
            {/* The "N devices are past their next-due date" banner that used
                to print here moved to the compliance card above the tabs. It
                was inside a collapsible (so it vanished when the section
                closed), scoped to one trade (so a two-trade site showed two
                numbers and no total), and carried a count with no age. It is
                site-wide, always visible and dated now. */}
            <AttachmentPreviewDialog
                open={!!previewAttachment}
                onClose={() => setPreviewAttachment(null)}
                attachmentId={previewAttachment?.id ?? ""}
                fileName={previewAttachment?.fileName ?? "Attachment"}
            />
        </CollapsibleSection>
    );
}

// ── Jobs ─────────────────────────────────────────────────────────────
